import {
  Box,
  Flex,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from '@chakra-ui/react';
import { useGrowthBook } from '@growthbook/growthbook-react';
import { GlucoseTab } from '@web-app/components/CallV2/GlucoseTab/GlucoseTab';
import { InsuranceV2 } from '@web-app/components/CallV2/InsuranceV2/InsuranceV2';
import { LabsTab } from '@web-app/components/CallV2/LabsTab/LabsTab';
import { MedicationsTab } from '@web-app/components/CallV2/MedicationsTab/MedicationsTab';
import { PatientInfoV2 } from '@web-app/components/CallV2/PatientInfoV2/PatientInfoV2';
import { WalmartInsightsBanner } from '@web-app/components/CallV2/WalmartInsights/WalmartInsightsBanner';
import { MedicareBridgeReviewPanel } from '@web-app/components/MedicareBridge/MedicareBridgeReviewPanel';
import { TAGS } from '@web-app/components/PatientTags/constants';
import { EVENT_NAMES, useAnalytics } from '@web-app/context/AnalyticsContext';
import { AppointmentResponse } from '@web-app/hooks/queries/appointment-by-room-name/types';
import { useGetPatientDetails } from '@web-app/hooks/queries/patient/useGetPatientDetails';
import { useAbbottCgmEnabled } from '@web-app/utils/feature-flags/abbott-cgm';
import { useLabResultsEnabled } from '@web-app/utils/feature-flags/lab-results';
import { useShowLabDocumentsTab } from '@web-app/utils/feature-flags/show-lab-documents-tab';
import { useWalmartShoppingInsights } from '@web-app/utils/feature-flags/walmart-shopping-insights';
import {
  getAppointmentTags,
  getPatientFirstName,
  hasPatientTag,
} from '@web-app/utils/patient-tags.utils';
import { useEffect, useState } from 'react';

const PERSONAL_SUB_TAB_INDEX = 0;
const LABS_SUB_TAB_INDEX = 2;

interface PatientSubTabsProps {
  appointment?: AppointmentResponse;
  isActive: boolean;
  roomId?: string;
}

/**
 * Sub-tabs component for Patient information section.
 * Contains "Personal" tab for patient info and "Insurance" tab for insurance details.
 * Used in the "Patient" tab of SidePane.
 */
export const PatientSubTabs = ({
  appointment,
  isActive,
}: PatientSubTabsProps) => {
  const { trackEvent } = useAnalytics();
  const showLabDocumentsTab = useShowLabDocumentsTab();
  const showWalmartInsights = useWalmartShoppingInsights();
  const abbottCgmEnabled = useAbbottCgmEnabled();
  const labResultsEnabled = useLabResultsEnabled();
  // Render later conditional tabs only once flags have loaded, so Labs and
  // Medications can't shift in and steal another tab's numeric index.
  const flagsReady = useGrowthBook()?.ready ?? false;
  const [subTabIndex, setSubTabIndex] = useState(PERSONAL_SUB_TAB_INDEX);
  const showGlucoseTab = flagsReady && abbottCgmEnabled;
  // Lab results wait for flags so the Labs tab can't appear late and shift the tabs after it.
  const showLabResults = flagsReady && labResultsEnabled;
  const showLabsTab = showLabDocumentsTab || showLabResults;
  const glucoseSubTabIndex = 3 + Number(showLabsTab);

  const patientFirstName = getPatientFirstName(
    appointment?.healthie?.user?.name,
  );
  const clientId = appointment?.client_id?.toString() ?? '';
  const appointmentId = appointment?.healthie_id?.toString() ?? '';
  const { isPending: isPatientLoading } = useGetPatientDetails(clientId);
  const showWalmartInsightsBanner =
    showWalmartInsights &&
    !isPatientLoading &&
    !!appointmentId &&
    hasPatientTag(getAppointmentTags(appointment), TAGS.WALMART);

  useEffect(() => {
    if (isActive) {
      trackEvent(EVENT_NAMES.CLICKS_PATIENT_INFO);
    }
  }, [trackEvent, isActive]);

  if (!appointment?.client_id) {
    return null;
  }

  return (
    <Box
      h="100%"
      display="flex"
      flexDirection="column"
      bg="surface.beige"
      data-testid="patient-subtabs-wrapper"
    >
      <Tabs
        size="sm"
        variant="unstyled"
        colorScheme="green"
        index={subTabIndex}
        onChange={setSubTabIndex}
        data-testid="patient-subtabs"
      >
        <TabList
          borderBottom="1px solid"
          borderColor="green.200"
          overflowX="auto"
          flexShrink={0}
          sx={{ '> [role="tab"]': { flexShrink: 0 } }}
          data-testid="patient-subtabs-tablist"
        >
          <Tab
            _selected={{
              color: 'green.600',
              borderBottom: '3px solid',
              borderBottomColor: 'green.600',
            }}
          >
            Personal
          </Tab>
          <Tab
            _selected={{
              color: 'green.600',
              borderBottom: '3px solid',
              borderBottomColor: 'green.600',
            }}
          >
            Insurance
          </Tab>
          {showLabsTab && (
            <Tab
              _selected={{
                color: 'green.600',
                borderBottom: '3px solid',
                borderBottomColor: 'green.600',
              }}
              data-testid="patient-subtab-labs"
            >
              Labs
            </Tab>
          )}
          {flagsReady && (
            <Tab
              _selected={{
                color: 'green.600',
                borderBottom: '3px solid',
                borderBottomColor: 'green.600',
              }}
              data-testid="patient-subtab-medications"
              onClick={() => trackEvent(EVENT_NAMES.MEDICATIONS_TAB_VIEWED)}
            >
              Medications
            </Tab>
          )}
          {showGlucoseTab && (
            <Tab
              flexShrink={0}
              _selected={{
                color: 'green.600',
                borderBottom: '3px solid',
                borderBottomColor: 'green.600',
              }}
              data-testid="patient-subtab-glucose"
            >
              Glucose
            </Tab>
          )}
        </TabList>

        <TabPanels
          flex="1"
          overflowY="auto"
          backgroundColor="surface.subtleAlt"
        >
          <TabPanel h="100%">
            <Box data-testid="patient-info-v2" h="100%" overflowY="auto">
              <Flex direction="column" p={6}>
                <MedicareBridgeReviewPanel
                  patientHealthieId={clientId}
                  appointmentHealthieId={appointmentId || undefined}
                />
                <PatientInfoV2 clientId={clientId} />
                {showWalmartInsightsBanner && (
                  <WalmartInsightsBanner
                    patientFirstName={patientFirstName}
                    patientId={clientId}
                    appointmentId={appointmentId}
                    isVisible={
                      isActive && subTabIndex === PERSONAL_SUB_TAB_INDEX
                    }
                  />
                )}
              </Flex>
            </Box>
          </TabPanel>
          <TabPanel h="100%">
            <Box data-testid="insurance-v2" h="100%" overflowY="auto">
              <Flex direction="column" p={6}>
                <InsuranceV2 clientId={clientId} />
              </Flex>
            </Box>
          </TabPanel>
          {showLabsTab && (
            <TabPanel h="100%" p={0}>
              <LabsTab
                patientId={clientId}
                isActive={isActive && subTabIndex === LABS_SUB_TAB_INDEX}
                showResults={showLabResults}
                showDocuments={showLabDocumentsTab}
              />
            </TabPanel>
          )}
          {flagsReady && (
            <TabPanel h="100%" p={0}>
              <MedicationsTab clientId={clientId} />
            </TabPanel>
          )}
          {showGlucoseTab && (
            <TabPanel h="100%" p={0}>
              <GlucoseTab
                patientId={clientId}
                isActive={isActive && subTabIndex === glucoseSubTabIndex}
              />
            </TabPanel>
          )}
        </TabPanels>
      </Tabs>
    </Box>
  );
};

PatientSubTabs.displayName = 'PatientSubTabs';
